import React from "react"
import { graphql } from "gatsby"
import styled from "styled-components"
import { GatsbyImage } from "gatsby-plugin-image"

import BlogLayout from "../layouts/Blog"
import { Container, Row, Col } from "../components/Grid"
import BodyText from "../components/BodyText"

import StyledAvatar from "../components/Avatar"
import Button from "../components/Button"
import Link from "../components/Link"
import Box from "../components/Box"
import ImageContentful from "../components/ImageContentful"
import FormNewsletter from "../components/FormNewsletter"
import Spacer from "../components/Spacer"
import Seo from "../components/Seo"
import Tag, { TagList } from "../components/Tag"
import PostTeaser from "../components/blog/PostTeaser"
import FuturaNewsletterLightbox from "../components/FuturaNewsletterLightbox"
import SocialShare from "../components/SocialShareVertical"

import { makeFastlink } from "../helpers"
import { media, mixins } from "../styles"
import { headerHeightLg } from "../components/Header"

export const pageQuery = graphql`
  query ContentfulBlogPostPageQuery($slug: String, $originalPath: String) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentfulPageMetadata(
      node_locale: { eq: "en" }
      pagePath: { eq: $originalPath }
    ) {
      ...PageMetadata
    }
    contentfulBlogPost(slug: { eq: $slug }, node_locale: { eq: "en" }) {
      title
      date(formatString: "LL", locale: "en")
      author {
        nameFull
        picture {
          ...ContentfulImageData
          gatsbyImageData(
            cropFocus: FACE
            height: 90
            layout: CONSTRAINED
            quality: 90
            resizingBehavior: THUMB
            width: 90
          )
        }
      }
      categories {
        name
        slug
      }
      coverImage {
        ...ContentfulImageData
        gatsbyImageData(
          height: 1100
          layout: CONSTRAINED
          quality: 75
          resizingBehavior: THUMB
          width: 1850
          placeholder: BLURRED
        )
      }
      socialShareImageOpengraph: coverImage {
        ...ContentfulImageData
        gatsbyImageData(
          layout: FIXED
          width: 1200
          height: 628
          quality: 85
          formats: JPG
          resizingBehavior: THUMB
        )
      }
      socialShareImageTwitter: coverImage {
        ...ContentfulImageData
        gatsbyImageData(
          layout: FIXED
          width: 1024
          height: 512
          quality: 85
          formats: JPG
          resizingBehavior: THUMB
        )
      }
      ctaType
      ctaHeader
      ctaLinkText
      ctaLinkUrl
      metaDescription
      keywords
      description
      text {
        childMarkdownRemark {
          excerpt(truncate: true, pruneLength: 140, format: PLAIN)
          html
          timeToRead
        }
      }
      slug
    }
    relatedPosts: allContentfulBlogPost(
      filter: { slug: { ne: $slug }, node_locale: { eq: "en" } }
      sort: { date: DESC }
      limit: 2
    ) {
      nodes {
        coverImage {
          ...ContentfulImageData
          gatsbyImageData(
            layout: CONSTRAINED
            width: 570
            height: 340
            placeholder: BLURRED
          )
        }
        date(formatString: "LL", locale: "en")
        description
        categories {
          name
          slug
        }
        slug
        title
      }
    }
    fallbackAvatar: file(relativePath: { eq: "logos/solaris-signet.png" }) {
      childImageSharp {
        gatsbyImageData(width: 45, height: 45, layout: CONSTRAINED)
      }
    }
  }
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.greyLight};
  padding: 30px 0;

  @media ${media.md} {
    padding: 65px 0 5px;
  }

  @media ${media.xl} {
    padding: 95px 0 0;
  }
`

const Header = styled.div`
  margin: 0 0 20px;
  padding: 15px 0 0;

  @media ${media.sm} {
    background: none;
  }

  @media ${media.xl} {
    margin: 0 0 25px;
    padding: 35px 0 0;
  }
`

const PostTitle = styled.h1`
  ${mixins.title3}
  margin: 0 0 15px;

  @media ${media.xl} {
    margin: 0 0 25px;
  }
`

const PostBody = styled.div`
  background: ${({ theme }) => theme.white};
  padding: 30px 15px 40px;
  margin: 0 -15px;

  @media ${media.sm} {
    padding: 0;
    margin: 0;
  }

  @media print {
    padding-bottom: 0;
  }
`

const TextWidth = styled.div`
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
`

const Content = styled.div`
  @media ${media.sm} {
    background: ${({ theme }) => theme.white};
    margin: 0 20px;
    padding: 20px 30px 60px;
  }

  @media ${media.lg} {
    margin: 0 auto;
    max-width: 720px;
    padding: 20px 35px 60px;
  }

  @media ${media.xl} {
    margin: 0 auto;
    padding: 20px 50px 100px;
    max-width: 1024px;
  }
`

const CoverImage = styled.div`
  margin: 0 -15px;

  @media ${media.sm} {
    margin: 0 0 30px;
  }

  @media ${media.lg} {
    margin: 0 0 35px;
  }

  @media ${media.xl} {
    margin: 0 0 55px;
  }
`

const ReadingTime = styled.p`
  color: ${({ theme }) => theme.sage};
  margin: 0 0 20px;

  @media ${media.xl} {
    margin: 0 0 30px;
  }
`

const PostMeta = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row wrap;
  ${mixins.copy5};

  @media ${media.smDown} {
    font-size: 14px;
  }
`

const Avatar = styled(StyledAvatar)``

const Author = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.sage};
  display: flex;
  margin: 0 0 10px;

  ${Avatar} {
    background: transparent;
    flex: none;
    margin-right: 10px;
  }
`

const AuthorName = styled.span`
  color: ${({ theme }) => theme.spaceblue};
  display: block;
  margin: 0 10px 0 5px;
  white-space: nowrap;
`

const PostDate = styled.span`
  color: ${({ theme }) => theme.sage};
  display: block;
  margin: 0 0 10px;
  white-space: nowrap;
`

const Share = styled.div`
  margin: 0 0 60px;

  @media ${media.lg} {
    margin: 0 0 30px;
    position: sticky;
    top: ${headerHeightLg + 20}px;
  }
`

const CtaBox = styled(Box)`
  @media print {
    display: none;
  }
`

const Cta = styled.div`
  padding: 25px 30px 30px;
  text-align: center;

  @media ${media.md} {
    padding: 30px 40px 40px;
  }

  @media ${media.xl} {
    padding: 35px 60px 40px;
  }

  h3 {
    ${mixins.copy3}
    margin: 0 0 20px;

    @media ${media.md} {
      margin: 0 0 30px;
    }

    @media ${media.xl} {
      margin: 0 0 15px;
    }
  }

  a {
    text-decoration: none;
  }
`

const NewsletterFormWrapper = styled.div`
  padding: 0 30px;

  h3 {
    ${mixins.title5}
    text-align: center;
  }

  .button {
    display: block;
    margin: 0 auto;
  }

  @media print {
    display: none;
  }
`

const PostList = styled.div`
  display: flex;
  flex-direction: column;

  @media ${media.md} {
    padding: 0 15px;
    flex-flow: row wrap;
    justify-content: space-between;
  }

  @media ${media.xl} {
    padding: 0 80px;
  }

  .post-teaser {
    margin: 0 0 30px;

    @media ${media.md} {
      width: calc(50% - 15px);
    }

    @media ${media.xl} {
      margin: 0 0 80px;
      width: calc(50% - 50px);
    }
  }
`

const Related = styled.div`
  h3 {
    ${mixins.title5}
    color: ${({ theme }) => theme.sage};
    text-align: center;
    margin: 0 0 25px;

    @media ${media.xl} {
      margin: 0 0 65px;
    }
  }

  @media print {
    display: none;
  }
`

const ContentfulBlogPostTemplate = (props) => {
  const post = props.data.contentfulBlogPost
  const pageMetadata = props.data.contentfulPageMetadata
  const relatedPosts = props.data.relatedPosts.nodes
  const socialShareImages = {}

  if (post.socialShareImageOpengraph) {
    socialShareImages.socialShareImageOpengraph =
      post.socialShareImageOpengraph.gatsbyImageData.images.fallback.src
  }

  if (post.socialShareImageTwitter) {
    socialShareImages.socialShareImageTwitter =
      post.socialShareImageTwitter.gatsbyImageData.images.fallback.src
  }

  return (
    <>
      <Seo
        {...pageMetadata}
        title={`${post.title} | Blog`}
        description={
          post.metaDescription ||
          post.description ||
          post.text?.childMarkdownRemark?.excerpt
        }
        lang="en"
        {...socialShareImages}
      />
      <BlogLayout pageId={`blog/${post.slug}`}>
        <Wrapper>
          <Container>
            <Row>
              <Col lg="10" lgOffset="1">
                <Content>
                  {post.categories ? (
                    <TagList>
                      {post.categories.map((category) => (
                        <Tag
                          as={Link}
                          intl={false}
                          key={category.name}
                          secondary
                          to={`/blog/tag/${category.slug}/`}
                        >
                          {category.name}
                        </Tag>
                      ))}
                    </TagList>
                  ) : null}
                  <Header>
                    <PostTitle>{post.title}</PostTitle>
                    {post.text ? (
                      <ReadingTime>
                        {post.text?.childMarkdownRemark?.timeToRead} minute read
                      </ReadingTime>
                    ) : null}
                    <PostMeta>
                      <Author>
                        <Avatar size={45}>
                          {post.author?.picture ? (
                            <ImageContentful
                              alt={post.author?.nameFull || "Solaris"}
                              image={post.author.picture}
                            />
                          ) : (
                            <GatsbyImage
                              alt="Solaris"
                              image={
                                props.data.fallbackAvatar.childImageSharp
                                  .gatsbyImageData
                              }
                            />
                          )}
                        </Avatar>
                        By{" "}
                        <AuthorName>
                          {post.author?.nameFull || "Solaris"}
                        </AuthorName>
                      </Author>
                      <PostDate>{post.date}</PostDate>
                    </PostMeta>
                  </Header>
                  {post.coverImage && (
                    <CoverImage>
                      <ImageContentful
                        alt={post.title}
                        image={post.coverImage}
                      />
                    </CoverImage>
                  )}
                  <PostBody>
                    <Row>
                      <Col lg="9" lgOrder="2" xl="10">
                        <TextWidth>
                          <BodyText>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: post.text?.childMarkdownRemark?.html,
                              }}
                            />
                          </BodyText>
                        </TextWidth>
                      </Col>
                      <Col lg="2" lgOrder="1" xl="1">
                        <Share>
                          <SocialShare
                            text={post.title}
                            url={`${props.data.site.siteMetadata.siteUrl}/blog/${post.slug}/`}
                          />
                        </Share>
                      </Col>
                    </Row>
                    <TextWidth>
                      {post.ctaType === "Futura Newsletter Signup" ? (
                        <FuturaNewsletterLightbox />
                      ) : (
                        post.ctaHeader &&
                        post.ctaLinkUrl && (
                          <CtaBox>
                            <Cta>
                              <h3>{post.ctaHeader}</h3>
                              <Link to={makeFastlink(post.ctaLinkUrl)}>
                                <Button as="span" outline arrow>
                                  {post.ctaLinkText || "Learn more"}
                                </Button>
                              </Link>
                            </Cta>
                          </CtaBox>
                        )
                      )}
                    </TextWidth>
                  </PostBody>
                </Content>
              </Col>
            </Row>
            <Spacer height={50} xl={85} print={0} />
            <Related>
              <h3>Further stories from our blog</h3>
              <PostList>
                {relatedPosts.map((relatedPost) => (
                  <PostTeaser key={relatedPost.slug} post={relatedPost} />
                ))}
              </PostList>
            </Related>
          </Container>
        </Wrapper>
        <Spacer height={30} md={85} print={0} />
        <Container>
          <Row>
            <Col md="10" mdOffset="1" lg="8" lgOffset="2">
              <NewsletterFormWrapper>
                <h3>
                  Never miss an update - Sign up to the Solaris newsletter
                </h3>
                <FormNewsletter />
              </NewsletterFormWrapper>
            </Col>
          </Row>
        </Container>
        <Spacer height={30} md={60} xl={100} print={0} />
      </BlogLayout>
    </>
  )
}

export default ContentfulBlogPostTemplate
